/* eslint-disable prettier/prettier */
/* eslint-disable no-unused-vars */
import { yupResolver } from "@hookform/resolvers/yup";
import Loader from "common/Loader";
import { setToaster } from "common/toastStore";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import AdminServices from "services/admin-services";
import { IAddInvestor } from "types/customers.type";
import * as Yup from "yup";
import Select from "react-select";
import TokenService from "services/TokenService";
import { handlingUrlParams } from "Utils/AppUtils";
import { useNavigate } from "react-router";

interface AddUsersProps {
  onSuccess?: (response: any, isBondInvestor: boolean) => void;
  mode?: "add" | "edit";
  investorDetails?: any;
  onHide?: () => void;
}

const AddInvestor: FunctionComponent<AddUsersProps> = ({
  onSuccess,
  mode,
  investorDetails,
  onHide,
}) => {
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState<any>([]);
  const [criteriaList, setCriteriaList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedCriteria, setSelectedCriteria] = useState<any>(null);
  const [bondInvestor, setBondInvestor] = useState(false);
  const [loading, setLoading] = useState(false);

  const getCusomerObj = TokenService.getCustomerData();

  const validationSchema = Yup.object().shape({
    customerId: Yup.number().required("Customer ID is required"),
    name: Yup.string().required("Name is required"),
    externalInvestorName: Yup.string().required(
      "External Investor Name is required",
    ),
    description: Yup.string().required("Description is required"),
    pullCriteria: Yup.string().required("Pull Criteria required"),
    enableClassification: Yup.boolean().optional(),
    isActive: Yup.boolean().optional(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<IAddInvestor>({
    resolver: yupResolver(validationSchema),
    mode: "all",
    defaultValues: {
      enableClassification: false,
      isActive: true,
    },
  });

  useEffect(() => {
    if (mode === "edit" && investorDetails) {
      // -------- form fields --------
      setValue("customerId", investorDetails.customerId);
      setValue("name", investorDetails.name);
      setValue("description", investorDetails.description);
      setValue("externalInvestorName", investorDetails.externalInvestorName);
      setValue(
        "enableClassification",
        investorDetails.enableClassification || false,
      );
      setValue("isActive", investorDetails.isActive ?? true);

      setBondInvestor(investorDetails.isBondInvestor || false);

      const customer = {
        label: investorDetails.customerName,
        value: investorDetails.customerId,
      };
      setSelectedCustomer(customer);

      getPullCriteria(investorDetails.customerId);
    }
  }, [mode, investorDetails]);

  useEffect(() => {
    if (mode === "edit" && investorDetails && criteriaList.length > 0) {
      const matched = criteriaList.find(
        (c: any) => c.value === investorDetails?.pullCriteria?.id,
      ) as any;

      if (matched) {
        setSelectedCriteria(matched);
        setValue("pullCriteria", matched.value, { shouldValidate: true });
      }
    }
  }, [criteriaList, investorDetails, mode]);

  // ------------------ SUBMIT ------------------
  const onSubmit = (data: IAddInvestor) => {
    setLoading(true);
    console.log(data);
    const payload: any = {
      id: mode === "edit" ? investorDetails?.id : 0,
      customerId: data.customerId,
      name: data.name,
      description: data.description,
      pullCriteria:
      {
        id: data.pullCriteria,
        name: selectedCriteria?.label,
      },
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      enableClassification: data.enableClassification,
      externalInvestorName: data.externalInvestorName,
      isBondInvestor: bondInvestor,
      isActive: mode === "edit" ? data.isActive ?? true : true,
    };

    if (mode === "edit") {
      const existingServicerProductCodes = Array.isArray(
        investorDetails?.servicerProductCodes,
      )
        ? investorDetails.servicerProductCodes
        : null;

      payload.servicerProductCodes = bondInvestor
        ? existingServicerProductCodes || []
        : undefined;
      payload.pullCriteriaFilter = investorDetails?.pullCriteriaFilter;

      if (!bondInvestor) {
        delete payload.servicerProductCodes;
      }
    }

    const apiCall =
      mode === "edit"
        ? AdminServices.updateInvestor(payload)
        : AdminServices.addInvestor(payload);

    apiCall
      .then((response) => {
        setLoading(false);

        setToaster({
          severity: "success",
          summary: "Success",
          detail:
            mode === "edit"
              ? "Investor updated successfully!"
              : "Investor added successfully!",
        });

        onSuccess?.(response, bondInvestor);
        reset();

        setSelectedCustomer(null);
        setSelectedCriteria(null);
        setBondInvestor(false);
        if (mode === "edit") {
          onHide?.();
        }
      })
      .catch((e: any) => {
        setLoading(false);
        setToaster({
          severity: "error",
          summary: "Error Message",
          detail: `${e?.response?.data}`,
        });
      });
  };

  // ------------------ CUSTOMER ------------------
  const handleCustomerChange = (selected: any) => {
    setSelectedCustomer(selected);
    setValue("customerId", selected?.value, { shouldValidate: true });

    getPullCriteria(selected?.value);
  };

  const getCusomerData = () => {
    const customerDataArray =
      getCusomerObj?.map((c: any) => ({
        label: c.name,
        value: c.id,
      })) || [];

    setCustomerData(customerDataArray);

    if (mode === "edit") return;

    if (customerDataArray.length === 1) {
      const single = customerDataArray[0];

      setSelectedCustomer(single);
      setValue("customerId", single.value, { shouldValidate: true });

      getPullCriteria(single.value);
    }
  };

  useEffect(() => {
    getCusomerData();
  }, []);

  const handleCriteriaChange = (selected: any) => {
    setSelectedCriteria(selected);
    setValue("pullCriteria", selected?.value, { shouldValidate: true });
  };

  const getPullCriteria = (customerId: number) => {
    AdminServices.getPullCriteriaByID(handlingUrlParams({ customerId }))
      .then((res: any) => {
        const list =
          res?.data?.map((pkg: any) => ({
            label: pkg.name,
            value: pkg.id,
          })) || [];

        setCriteriaList(list);
      })
      .catch(console.error);
  };

  // ------------------ UI ------------------
  return (
    <>
      <h2 className="pb-3 text-2xl font-medium">Add Investor</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-8">
          {/* Customer */}
          <div>
            <Select
              options={customerData}
              placeholder="Select customer"
              value={selectedCustomer}
              onChange={handleCustomerChange}
            />
            {errors.customerId && (
              <div className="text-xs text-red-900 mt-1">
                {errors.customerId.message}
              </div>
            )}
          </div>

          {/* Name */}
          <div className="">
            <InputText
              id="name"
              {...register("name")}
              className="w-full"
              placeholder="Enter Name"
            />

            <div className="text-xs text-red-900">{errors.name?.message}</div>
          </div>

          {/* Description */}
          <div className="">
            <InputText
              id="description"
              {...register("description")}
              className="w-full"
              placeholder="Enter Description"
            />

            <div className="text-xs text-red-900">
              {errors.description?.message}
            </div>
          </div>

          {/* External Name */}
          <div className="">
            <InputText
              placeholder="Enter External Investor Name"
              id="externalInvestorName"
              {...register("externalInvestorName")}
              className="w-full"
            />

            <div className="text-xs text-red-900">
              {errors.externalInvestorName?.message}
            </div>
          </div>

          {/* Criteria */}
          <div>
            <Select
              options={criteriaList}
              placeholder="Select Criteria"
              value={selectedCriteria}
              onChange={handleCriteriaChange}
            />
            <div className="text-xs text-red-900">
              {errors.pullCriteria?.message}
            </div>
          </div>
        </div>

        {/* Checkbox */}
        <div className="flex mt-6">
          <Checkbox
            inputId="enableClassification"
            checked={watch("enableClassification") || false}
            onChange={(e) =>
              setValue("enableClassification", e.checked ?? false)
            }
          />
          <label className="px-3">Enable Classification</label>
        </div>

        {/* Bond */}
        <div className="flex mt-4">
          <Checkbox
            inputId="isBondInvestor"
            checked={bondInvestor}
            onChange={(e) => setBondInvestor(e.checked ?? false)}
          />
          <label className="px-3">Is Bond Investor</label>
        </div>

        {mode === "edit" && (
          <div className="flex mt-4">
            <Checkbox
              inputId="isActive"
              checked={watch("isActive") || false}
              onChange={(e) =>
                setValue("isActive", e.checked ?? false, {
                  shouldValidate: true,
                })
              }
            />
            <label className="px-3" htmlFor="isActive">
              Is Active
            </label>
          </div>
        )}

        {/* Actions */}
        <div className="mt-8 flex flex-row-reverse">
          <Button
            label={
              mode === "edit"
                ? "Update"
                : bondInvestor
                  ? "Add Product Codes"
                  : "Save"
            }
            type="submit"
            icon={
              mode === "edit"
                ? "pi pi-check"
                : bondInvestor
                  ? "pi pi-plus"
                  : "pi pi-check"
            }
            className="p-button-sm p-button-success p-button-outlined"
          />
          <div className="mx-2"></div>
          <Button
            type="button"
            label="Cancel"
            icon="pi pi-times"
            onClick={() => {
              mode === "edit" ? onHide?.() : navigate("/investor");
            }}
            className="p-button-sm p-button-danger p-button-outlined"
          />
        </div>
      </form>

      {loading && <Loader />}
    </>
  );
};

export default AddInvestor;
